var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { observable, action, autorun, extendObservable, makeObservable } from 'mobx';
import { toJSTransformer as toJS } from 'framework/utils/CommonUtils';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _merge from 'lodash/merge';
import { getComputedFinancing, normalizeRateObject as normalizeRateObjectBase } from '@bytebrand/fe-financing';
import WpAPI from '../services/WpAPI';
import { FOURTEEN, THOUSAND } from '../framework/constants/app';
import { offers } from '@bytebrand/fe-ui-core-autobahn/constants';
import VehicleDetailsStore from './VehicleDetailsStore';
class FinancingStore {
    constructor() {
        Object.defineProperty(this, "userConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        // Sparse per-car user adjustments: carId -> only the fields the user actually
        // touched (OfferBlock stamps comparable.isUpdated on every change). This map —
        // not the fully-computed userConfig — is what gets persisted, and it replaces
        // the old global userCommonConfig that leaked one car's changes into all cars.
        Object.defineProperty(this, "userCarAdjustments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "searchConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "requestedOffers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "checkoutList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "offerIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "financingConfigId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "setUserConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, config) => {
                if (!!config && !!carId) {
                    // Re-apply the user's own adjustments for THIS car over the incoming
                    // dealer defaults (per-car replacement of the old global merge).
                    _merge(config, this.getUserCarAdjustments(carId));
                    const calculatedInputs = getComputedFinancing(config);
                    const offerName = offers[this.offerIndex].name;
                    const financingConfig = { ...config, ...calculatedInputs, carId, offerName, vehicleId: carId, offerIndex: this.offerIndex,
                        leasing: { ...config.leasing, ...calculatedInputs.leasing },
                        financing: { ...config.financing, ...calculatedInputs.financing }
                    };
                    this.userConfig.set(carId, financingConfig);
                    if (this.financingConfigId !== carId && !!toJS(this.userConfig)[carId].financing.monthlyInstallment)
                        this.financingConfigId = carId; // set financing config id for observable change
                }
            }
        });
        // Records a user's change for ONE car only (name kept for call-site
        // compatibility — it used to write a global config shared by all cars).
        Object.defineProperty(this, "setCommonUserConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, config) => {
                const carAdjustments = _merge(this.getUserCarAdjustments(carId), toJS(config));
                this.userCarAdjustments.set(carId, carAdjustments);
                const newConfig = _merge(this.getUserConfig(carId), carAdjustments);
                const calculatedInputs = getComputedFinancing(newConfig);
                const offerName = offers[this.offerIndex].name;
                const financingConfig = {
                    ...newConfig, ...calculatedInputs, carId, offerName, vehicleId: carId, offerIndex: this.offerIndex,
                    leasing: { ...newConfig.leasing, ...calculatedInputs.leasing },
                    financing: { ...newConfig.financing, ...calculatedInputs.financing }
                };
                VehicleDetailsStore.updateRecommendationCars();
                this.userConfig.set(carId, financingConfig);
                if (this.financingConfigId !== carId && !!toJS(this.userConfig)[carId].financing.monthlyInstallment)
                    this.financingConfigId = carId; // set financing config id for observable change
            }
        });
        Object.defineProperty(this, "getMaxFinancingFirstInstallment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                const userConfig = this.getUserConfig(carId);
                const { common: { currentSalesPrice, highestPrice }, financing: { periodInterestRate = [], withFinalInstallment, paybackPeriod, addServicesCostRaw } } = userConfig;
                const periodObj = periodInterestRate.find((el) => el.duration === paybackPeriod);
                const isCarOlderOneYear = true;
                const currentSalesPriceNoPresales = currentSalesPrice - addServicesCostRaw;
                const basicFinalInstallment = withFinalInstallment && paybackPeriod <= 72
                    ? _get(periodObj, 'inEUR') || _get(periodObj, 'current') * (isCarOlderOneYear ? currentSalesPriceNoPresales : highestPrice) / 100
                    : 0;
                return Math.floor((currentSalesPriceNoPresales - basicFinalInstallment) / 100) * 100;
            }
        });
        Object.defineProperty(this, "getUserConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleId) => {
                return this.userConfig.has(vehicleId)
                    ? this.userConfig.get(vehicleId)
                    : { financing: {}, leasing: {}, buy: {}, abo: {}, common: {}, checkout: {} };
            }
        });
        Object.defineProperty(this, "hasUserConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleId) => this.userConfig.has(vehicleId)
        });
        // Plain (non-observable) copy of the sparse adjustments the user made to
        // this specific car; {} when the car was never touched.
        Object.defineProperty(this, "getUserCarAdjustments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleId) => toJS(this.userCarAdjustments.get(vehicleId)) || {}
        });
        Object.defineProperty(this, "setSearchConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (config) => {
                this.searchConfig = config;
            }
        });
        Object.defineProperty(this, "clearSearchConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.searchConfig = {};
            }
        });
        Object.defineProperty(this, "normalizeRateObject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (car) => {
                const { price: { currentSalesPrice }, rate: defaultRate, leasing: defaultLeasing, abo: defaultAbo } = car;
                const searchPaybackPeriod = _get(this.searchConfig, 'paybackPeriod');
                const defaultPaybackPeriod = _get(defaultRate, 'paybackPeriod');
                const searchPurchasePrice = _get(this.searchConfig, 'purchasePrice');
                const defaultPurchasePrice = _get(defaultRate, 'purchasePrice');
                const searchFirstInstallment = _get(this.searchConfig, 'firstInstallment');
                const defaultFirstInstallment = _get(defaultRate, 'firstInstallment');
                const paybackPeriod = Number.isFinite(+searchPaybackPeriod) ? +searchPaybackPeriod : defaultPaybackPeriod;
                const purchasePrice = Number.isFinite(+searchPurchasePrice)
                    ? searchPurchasePrice * 100 / currentSalesPrice // eslint-disable-line
                    : defaultPurchasePrice;
                const firstInstallment = Number.isFinite(+searchFirstInstallment)
                    ? searchFirstInstallment * 100 / currentSalesPrice // eslint-disable-line
                    : defaultFirstInstallment;
                // leasing
                const searchLeasingPaybackPeriod = _get(this.searchConfig, 'leasing.paybackPeriod');
                const leasingDefaultPaybackPeriod = _get(defaultLeasing, 'paybackPeriod');
                const leasingSearchFirstInstallment = _get(this.searchConfig, 'leasing.firstInstallment');
                const leasingDefaultFirstInstallment = _get(defaultLeasing, 'firstInstallment');
                const searchLeasingMonthlyAddServices = _get(this.searchConfig, 'leasing.monthlyAddServices');
                const defaultLeasingMonthlyAddServices = _get(defaultLeasing, 'monthlyAddServices');
                const searchLeasingMonthlyTransportation = _get(this.searchConfig, 'leasing.monthlyTransportation');
                const defaultLeasingMonthlyTransportation = _get(defaultLeasing, 'monthlyTransportation');
                const leasingMonthlyAddServices = searchLeasingMonthlyAddServices || defaultLeasingMonthlyAddServices;
                const leasingMonthlyTransportation = searchLeasingMonthlyTransportation || defaultLeasingMonthlyTransportation;
                const leasingPaybackPeriod = Number.isFinite(+searchLeasingPaybackPeriod) ? +searchLeasingPaybackPeriod : leasingDefaultPaybackPeriod;
                const leasingFirstInstallment = Number.isFinite(+leasingSearchFirstInstallment)
                    ? leasingSearchFirstInstallment * 100 / currentSalesPrice // eslint-disable-line
                    : leasingDefaultFirstInstallment;
                // abo
                const aboSearchPaybackPeriod = _get(this.searchConfig, 'abo.paybackPeriod');
                const aboDefaultPaybackPeriod = _get(defaultAbo, 'paybackPeriod');
                const aboSearchFirstInstallment = _get(this.searchConfig, 'abo.firstInstallment');
                const aboDefaultFirstInstallment = _get(defaultAbo, 'firstInstallment');
                const aboPaybackPeriod = Number.isFinite(+aboSearchPaybackPeriod) ? +aboSearchPaybackPeriod : aboDefaultPaybackPeriod;
                const aboFirstInstallment = Number.isFinite(+aboSearchFirstInstallment)
                    ? aboSearchFirstInstallment * 100 / currentSalesPrice // eslint-disable-line
                    : aboDefaultFirstInstallment;
                _set(car, 'rate.paybackPeriod', paybackPeriod);
                _set(car, 'rate.purchasePrice', purchasePrice);
                _set(car, 'rate.firstInstallment', firstInstallment);
                _set(car, 'leasing.paybackPeriod', leasingPaybackPeriod);
                _set(car, 'leasing.firstInstallment', leasingFirstInstallment);
                _set(car, 'leasing.monthlyAddServices', leasingMonthlyAddServices);
                _set(car, 'leasing.monthlyTransportation', leasingMonthlyTransportation);
                _set(car, 'abo.paybackPeriod', aboPaybackPeriod);
                _set(car, 'abo.firstInstallment', aboFirstInstallment);
                const financingConfig = this.getUserConfig(car._id);
                const normalizedObj = {
                    ...car,
                    ...financingConfig,
                    financing: {
                        ...financingConfig.financing
                    },
                    leasing: {
                        ...car.leasing,
                        ...financingConfig.leasing
                    }
                };
                return normalizeRateObjectBase(normalizedObj);
            }
        });
        Object.defineProperty(this, "setOfferIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (index) => { this.offerIndex = index; }
        });
        Object.defineProperty(this, "getOfferIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.offerIndex
        });
        Object.defineProperty(this, "setRequestedOffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleId) => {
                this.requestedOffers.set(vehicleId, true);
            }
        });
        Object.defineProperty(this, "setCheckoutList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleId) => {
                this.checkoutList.set(vehicleId, true);
            }
        });
        Object.defineProperty(this, "clearRequestedOffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.requestedOffers.clear();
            }
        });
        makeObservable(this);
        this.load();
        autorun(() => {
            // Persist only adjustments younger than 14 days (comparable.isUpdated is
            // the "user touched this car" stamp, so untouched cars are never stored).
            const carAdjustments = toJS(this.userCarAdjustments);
            const persistedAdjustments = {};
            const max14DaysExpirePeriod = (new Date().setDate(new Date().getDate() - FOURTEEN)) / THOUSAND;
            for (const vehicleId in carAdjustments) {
                const currentConfig = carAdjustments[vehicleId];
                if (currentConfig.comparable && currentConfig.comparable.isUpdated && currentConfig.comparable.isUpdated > max14DaysExpirePeriod) {
                    persistedAdjustments[vehicleId] = currentConfig;
                }
            }
            // fe-wp-widgets shares this localStorage key on the same domain — keep
            // its keys (userConfig) intact, but never write the deprecated global
            // userCommonConfig back.
            let existing = {};
            try {
                existing = JSON.parse(localStorage.getItem('financing_store')) || {};
            }
            catch (e) {
                existing = {};
            }
            delete existing.userCommonConfig;
            const json = JSON.stringify({
                ...existing,
                searchConfig: this.searchConfig,
                userCarAdjustments: persistedAdjustments
            });
            localStorage.setItem('financing_store', json);
            window.dispatchEvent(new Event('storage'));
        });
    }
    load() {
        const json = localStorage.getItem('financing_store');
        if (json) {
            const financingStore = JSON.parse(json);
            // The legacy global userCommonConfig is intentionally dropped here:
            // adjustments are stored per car now, so old global values must not be
            // re-applied to every car (the autorun rewrites storage without them).
            const { userCarAdjustments = {} } = financingStore;
            const max14DaysExpirePeriod = (new Date().setDate(new Date().getDate() - FOURTEEN)) / THOUSAND;
            const freshAdjustments = {};
            for (const vehicleId in userCarAdjustments) {
                const currentConfig = userCarAdjustments[vehicleId];
                if (currentConfig.comparable && currentConfig.comparable.isUpdated && currentConfig.comparable.isUpdated > max14DaysExpirePeriod) {
                    freshAdjustments[vehicleId] = currentConfig;
                }
            }
            this.userCarAdjustments.merge(freshAdjustments);
            extendObservable(this.searchConfig, financingStore.searchConfig);
        }
    }
    addOfferToMainSlide(data) {
        return WpAPI.addSlide(data);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "userConfig", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "userCarAdjustments", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "searchConfig", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "requestedOffers", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "checkoutList", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "offerIndex", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FinancingStore.prototype, "financingConfigId", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setUserConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setCommonUserConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "getMaxFinancingFirstInstallment", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setSearchConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "clearSearchConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setOfferIndex", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "getOfferIndex", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setRequestedOffer", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "setCheckoutList", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], FinancingStore.prototype, "clearRequestedOffer", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], FinancingStore.prototype, "addOfferToMainSlide", null);
export default new FinancingStore();
